import React, { useState, useMemo } from 'react';

import { FormTitle, FormTitleAction } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { Table, TableCellPropsUnion } from '@components/Table/Table';
import { useSearch } from '@hooks/next/useSearch';
import { useCompanyItemList } from '@hooks/queries/dashboard/useCompanyItemList';
import { useUser } from '@hooks/queries/useUser';
import { allCompanyStatus, CompanyItem } from '@models/Company';
import { DashboardCompanySearchQuery } from '@models/next/Company';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import { addOrdering, DEFAULT_PAGE_SIZE, formatDateTime } from '@utils/types';
import { useChecklist } from '@utils/useChecklist';
import { Link } from 'react-router-dom';
import { pick } from 'remeda';

const strings = createStrings({
  ja: {
    title: (type: 'membershipForms' | 'completionRequests') =>
      ({
        membershipForms: '新規会員申し込み',
        completionRequests: 'D2D会員開設申請',
      }[type]),
    download: 'ダウンロード',
    todayCount: '今日の申込数',
    unassignedCount: '未対応数',
    assignedCount: '対応した件数',
    todayAssignedCount: '今日対応した件数',
    no: 'No.',
    approval_no: '申込番号',
    createdAt: '申し込み時間',
    companyName: '会社名',
    status: 'ステータス',
    salespersonName: '営業担当者',
    unassigned: '未対応',
    details: '詳細',
    seeAll: '全部見る' as const,
    registerTime: '登録時間',
    from: 'から',
    to: 'まで',
    searchTitle: '絞り込み',
    exportCSV: 'Export CSV',
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    selectOneItemAtLeast: '少なくとも1つのアイテムを選択してください',
    reset: 'リセット',
    myCases: '私の案件',
    tagSearchInputPlaceholder: '会社名、メールや客側担当者などの検索条件を入力してください',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px) 30px repeat(2, 1fr 30px)',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
  },
  searchButton: {
    alignSelf: 'flex-start',
  },
  joinLabel: {
    ...common.normalText,
    fontSize: 14,
    color: theme.palette.grey[700],
    margin: '0 0.5rem',
    paddingTop: 2,
  },
  startContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  inputContainer: {
    ...common.flexInline,
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
  },
  linkDetails: {
    color: theme.palette.primary.main,
  },
  dataGrid: {
    margin: '1rem 0',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    ...common.textAlignCenter,
    paddingTop: 5,
  },
}));

const MemberListSearchForm = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(DashboardCompanySearchQuery);

  const form = useForm(DashboardCompanySearchQuery, {
    initialOutput: search,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(
        pick(fields, ['company_name', 'approval_no', 'email', 'fullname']),
        form,
      ),
    [],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{ wrapper: { gridColumn: '1 / -1' } }}
      />

      <TextInput
        field={fields.created_at_after}
        inputProps={{
          placeholder: getFieldLabel(fields.created_at_after),
        }}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.created_at_before}
        inputProps={{
          placeholder: getFieldLabel(fields.created_at_before),
        }}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <div style={{ gridColumnEnd: '-1' }} />

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => {
            form.root.setOutput(empty);
          }}
        />
      </div>
    </form>
  );
};

export interface MemberListProps {
  type: 'membershipForms' | 'completionRequests';
}

export const MemberList: React.FC<MemberListProps> = ({ type }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(DashboardCompanySearchQuery);

  const query = {
    ...search,
    page: search.page ?? 1,
    page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    status: search.status ?? (type === 'membershipForms' ? undefined : 'D2D会員開設申請'),
  } satisfies DashboardCompanySearchQuery;

  const companyItemList = useCompanyItemList(query);
  const user = useUser();

  const checklist = useChecklist(
    [],
    (companyItemList?.data?.results ?? []).map((item) => item.id),
  );

  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const checkedItems = useMemo(() => {
    const items = (companyItemList.data?.results ?? [])
      .filter((item) => checklist.checked(item.id))
      .map((item) => item.id);
    return items;
  }, [companyItemList.data, checklist.checklist]);

  const actions: FormTitleAction[] = [
    {
      title: strings.exportCSV,
      action: () => {
        setExportDialogOpen(true);
      },
    },
  ];

  const orderingOptions = [
    {
      value: '-created_at',
      label: strings.fromTheNewTime,
    },
    {
      value: 'created_at',
      label: strings.fromTheOldTime,
    },
  ];

  const statusOptions = useMemo(() => {
    return [...allCompanyStatus, strings.seeAll]
      .map((status) => ({
        value: status,
        label: status,
      }))
      .filter((s) => !['ドラフト', '手続き完了', 'D2D会員開設申請'].includes(s.value));
  }, []);

  const salespersonOptions = [
    {
      value: true,
      label: strings.myCases,
    },
    {
      value: false,
      label: 'ALL',
    },
  ];

  const headColumns: TableCellPropsUnion[] = [
    {
      label: 'checkbox',
      type: 'checkable',
      checked: checklist.allCheckedAndNotEmpty,
      onChange: checklist.toggleCheckAll,
    },
    {
      label: strings.no,
    },
    {
      label: strings.approval_no,
    },
    {
      label: '',
    },
    {
      label: strings.createdAt,
      type: 'selectable',
      options: orderingOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          ordering: addOrdering(search.ordering ?? [], value),
        });
      },
    },
    {
      label: strings.companyName,
    },
    type === 'membershipForms'
      ? {
          label: strings.status,
          type: 'selectable',
          options: statusOptions,
          onChange: (value: any) => {
            const status = statusOptions.find((o) => o.value === value);

            if (status?.label === '全部見る') {
              updateSearch({ page: 1, status: undefined });
            } else {
              updateSearch({
                page: 1,
                status: value,
              });
            }
          },
        }
      : { label: strings.status },
    {
      label: strings.salespersonName,
      type: 'selectable',
      options: salespersonOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 1,
          salesperson_id: value ? user.data?.id : undefined,
        });
      },
    },
  ];

  function mapItemToTableRowData(
    index: number,
    item: CompanyItem,
    linkClassName: string,
  ): TableCellPropsUnion[] {
    return [
      {
        label: 'checkbox',
        type: 'checkable',
        checked: checklist.checked(item.id),
        onChange: (value: boolean) => checklist.setChecked(item.id, value),
      },
      {
        label: index + 1 + (query.page - 1) * query.page_size,
      },
      {
        label: item.approval_no || '-',
      },
      {
        label: (
          <Link
            to={
              type === 'membershipForms'
                ? `${url('/admin/members/MembershipFormDetails')}?id=${item.id}`
                : `${url('/admin/members/MemberDetails')}?id=${item.id}&type=completionRequest`
            }
            className={linkClassName}
          >
            {strings.details}
          </Link>
        ),
      },
      {
        label: formatDateTime(item.created_at),
      },
      {
        label: item.company_name || '-',
      },
      {
        label: item.status || strings.unassigned,
      },
      {
        label: item.salesperson_last_name || '-',
      },
    ];
  }

  const bodyRows = companyItemList.data?.results.map((result, index) => {
    return mapItemToTableRowData(index, result, classes.linkDetails);
  });

  return (
    <Layout
      title={strings.title(type)}
      loading={companyItemList.isLoading}
      errors={[companyItemList.error]}
    >
      {() => (
        <>
          <ExportCSVDialog
            open={exportDialogOpen}
            onClose={() => {
              setExportDialogOpen(false);
            }}
            subject={strings.title(type)}
            baseUrl="/dashboard/company/"
            query={query}
            selectedIds={checkedItems}
          />
          <div className={classes.container}>
            <FormTitle
              title={strings.title(type)}
              actions={actions}
              titleSize="large"
              lineDivider={false}
            />

            <MemberListSearchForm />

            <PageSizeForm />

            <div style={{ height: 16 }} />

            <Table>
              <Table.Head>
                <Table.Row columns={headColumns} />
              </Table.Head>
              <Table.Body>
                {bodyRows?.map((columns, index) => {
                  return <Table.Row key={index} columns={columns} />;
                })}
              </Table.Body>
            </Table>
            <br />
            <div className={classes.paginationContainer}>
              <PaginationWithSearchQuery query={companyItemList} pageSize={query.page_size} />
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};
